import { computed, ref } from 'vue';

import { extractGraphQLError } from '@/utils/graphqlError';

export interface TertiaryEntityFailure<T> {
  index: number;
  item: T;
  /** The server's reason, e.g. "Programme code BTH is already used by …". */
  message: string;
}

export interface TertiaryEntityWorkflowResult<T = unknown> {
  total: number;
  successful: number;
  failed: number;
  failures: TertiaryEntityFailure<T>[];
}

export interface SubmissionToast {
  severity: 'success' | 'warn';
  summary: string;
  detail: string;
  life: number;
}

const FAILURE_FALLBACK = 'This entry was not created. Please try again.';

/**
 * The summary toast after a queued submit. Failed entries stay in the queue
 * with their reason, so the wording points there instead of at the form.
 */
export function submissionToast(result: TertiaryEntityWorkflowResult, plural: string): SubmissionToast | null {
  if (result.total === 0) return null;

  if (result.failed === 0) {
    return { severity: 'success', summary: 'Success', detail: `${result.total} ${plural} created`, life: 3000 };
  }

  if (result.failed === result.total) {
    return {
      severity: 'warn',
      summary: 'Nothing was created',
      detail: `None of the ${plural} were created. The reason is shown next to each one in the queue.`,
      life: 6000,
    };
  }

  return {
    severity: 'warn',
    summary: 'Partial success',
    detail: `${result.successful} of ${result.total} ${plural} created. The rest are still in the queue with the reason next to each.`,
    life: 6000,
  };
}

export function useTertiaryEntityWorkflow<T>() {
  const pendingItems = ref<T[]>([]);
  /** Why each pending item failed on the last submit, aligned with pendingItems ('' = not tried yet). */
  const pendingErrors = ref<string[]>([]);
  const isSubmitting = ref(false);

  function addItem(item: T) {
    pendingItems.value.push(item as never);
    pendingErrors.value.push('');
  }

  function removeItem(index: number) {
    pendingItems.value.splice(index, 1);
    pendingErrors.value.splice(index, 1);
  }

  function clearItems() {
    pendingItems.value = [];
    pendingErrors.value = [];
  }

  async function submitAll<U>(
    items: U[],
    handler: (item: U) => Promise<void> | void,
  ): Promise<TertiaryEntityWorkflowResult<U>> {
    if (!items.length) {
      return {
        total: 0,
        successful: 0,
        failed: 0,
        failures: [],
      };
    }

    isSubmitting.value = true;

    let successful = 0;
    const failures: TertiaryEntityFailure<U>[] = [];

    try {
      for (const [index, item] of items.entries()) {
        try {
          await handler(item);
          successful++;
        } catch (error) {
          failures.push({ index, item, message: extractGraphQLError(error, FAILURE_FALLBACK) });
        }
      }

      return {
        total: items.length,
        successful,
        failed: failures.length,
        failures,
      };
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * Submit the queue. Created entries leave it; failed ones stay, each with
   * its reason in pendingErrors, so nothing the user typed is lost.
   */
  async function submitPending(handler: (item: T) => Promise<void> | void): Promise<TertiaryEntityWorkflowResult<T>> {
    const result = await submitAll(pendingItems.value as T[], handler);
    pendingItems.value = result.failures.map(failure => failure.item) as never;
    pendingErrors.value = result.failures.map(failure => failure.message);
    return result;
  }

  const hasPendingItems = computed(() => pendingItems.value.length > 0);

  return {
    pendingItems,
    pendingErrors,
    isSubmitting,
    hasPendingItems,
    addItem,
    removeItem,
    clearItems,
    submitAll,
    submitPending,
  };
}
